import { useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { mergeRegister } from '@lexical/utils';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  $createParagraphNode,
  $nodesOfType,
  EditorState,
  KEY_ENTER_COMMAND,
  COMMAND_PRIORITY_HIGH,
  LexicalEditor,
} from 'lexical';
import { 
  PromptItemNode, 
  $createPromptItemNode, 
  PromptItemPayload,
  $convertAllTentativeToPermanent,
  $removeAllTentativeNodes
} from './PromptItemNode';
import { PromptItemData } from '../../../utils/promptItemContext';
import {
  INSERT_TENTATIVE_ITEM_COMMAND,
  REMOVE_ALL_TENTATIVE_COMMAND,
  CONVERT_TENTATIVE_TO_PERMANENT_COMMAND,
  INPUT_FOCUSED_COMMAND,
} from './PromptCommands';
import TentativeItemPlugin from './TentativeItemPlugin';

export interface LexicalPromptInputHandle {
  insertItem: (payload: PromptItemPayload) => void;
  focus: () => void;
  getEditor: () => LexicalEditor | null;
  getPromptItems: () => PromptItemData[];
}

interface LexicalPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  onFocusChange?: (focused: boolean) => void;
}

// Plugin to handle Enter key
function EnterKeyPlugin({ onSubmit }: { onSubmit: () => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent | null) => {
        // Shift+Enter = newline (default behavior)
        if (event?.shiftKey) {
          return false;
        }

        // Enter = submit
        event?.preventDefault();
        onSubmit();
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, onSubmit]);

  return null;
}

// Flag to track when we're programmatically inserting tentative items
let isInsertingTentative = false;

// Plugin to handle focus/blur events
function FocusBlurPlugin({ onFocusChange }: { onFocusChange?: (focused: boolean) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handleFocus = () => {
      // Ignore focus events during tentative item insertion
      if (isInsertingTentative) {
        return;
      }
      
      editor.dispatchCommand(INPUT_FOCUSED_COMMAND, undefined);
      
      if (onFocusChange) {
        onFocusChange(true);
      }
    };
    
    const handleBlur = () => {
      if (onFocusChange) {
        onFocusChange(false);
      }
    };

    rootElement.addEventListener('focus', handleFocus);
    rootElement.addEventListener('blur', handleBlur);

    return () => {
      rootElement.removeEventListener('focus', handleFocus);
      rootElement.removeEventListener('blur', handleBlur);
    };
  }, [editor, onFocusChange]);

  return null;
}

// Plugin to register command handlers for tentative item operations
function CommandPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        INSERT_TENTATIVE_ITEM_COMMAND,
        (payload) => {
          // Check if editor is focused BEFORE the update
          const rootElement = editor.getRootElement();
          const wasFocused = rootElement && document.activeElement === rootElement;
          
          // Set flag to ignore focus events during insertion
          isInsertingTentative = true;
          
          // Use discrete: true to prevent the update from causing focus/selection changes
          editor.update(() => {
            const root = $getRoot();
            const itemNode = $createPromptItemNode({
              type: 'element',
              label: payload.label,
              id: `tentative-${payload.editId}`,
              isTentative: true,
              metadata: { editId: payload.editId },
            });

            let firstChild = root.getFirstChild();
            
            if (!firstChild) {
              // Create a paragraph if none exists
              const paragraph = $createParagraphNode();
              root.append(paragraph);
              firstChild = paragraph;
            }

            if (firstChild) {
              // Prepend to the first child's children (no space for tentative items)
              const existingChildren = (firstChild as any).getChildren();
              (firstChild as any).clear();
              (firstChild as any).append(itemNode);
              existingChildren.forEach((child: any) => (firstChild as any).append(child));
            }
          }, { discrete: true }); // Prevent focus changes
          
          // Clear flag and blur in a microtask to ensure it happens after Lexical's focus operations
          queueMicrotask(() => {
            isInsertingTentative = false;
            
            // If editor was NOT focused before, blur it now (after Lexical's focus is complete)
            if (!wasFocused && rootElement && document.activeElement === rootElement) {
              rootElement.blur();
            }
          });
          
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        REMOVE_ALL_TENTATIVE_COMMAND,
        () => {
          editor.update(() => {
            $removeAllTentativeNodes();
          });
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        CONVERT_TENTATIVE_TO_PERMANENT_COMMAND,
        () => {
          editor.update(() => {
            $convertAllTentativeToPermanent();
          });
          return true;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor]);

  return null;
}

// Plugin to expose methods via ref
function ImperativeHandlePlugin({ 
  imperativeRef 
}: { 
  imperativeRef: React.RefObject<LexicalPromptInputHandle | null> 
}) {
  const [editor] = useLexicalComposerContext();

  useImperativeHandle(imperativeRef, () => ({
    insertItem: (payload: PromptItemPayload) => {
      editor.update(() => {
        const itemNode = $createPromptItemNode(payload);
        const spaceNode = $createTextNode(' ');
        
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([itemNode, spaceNode]);
        } else {
          // No selection, append to end
          const root = $getRoot();
          let firstChild = root.getFirstChild();
          
          // Create a paragraph if none exists (important for unfocused editor)
          if (!firstChild) {
            const paragraph = $createParagraphNode();
            root.append(paragraph);
            firstChild = paragraph;
          }
          
          if (firstChild) {
            (firstChild as any).append(itemNode);
            (firstChild as any).append(spaceNode);
          }
        }
      });
    },
    focus: () => {
      editor.focus();
    },
    getEditor: () => editor,
    getPromptItems: () => {
      const items: PromptItemData[] = [];
      editor.getEditorState().read(() => {
        const promptItemNodes = $nodesOfType(PromptItemNode);
        for (const node of promptItemNodes) {
          // Include ALL items (tentative and permanent) when extracting for submission
          // The tentative flag is just for visual styling
          items.push({
            type: node.__itemType,
            id: node.__id,
            label: node.__label,
            isTentative: node.__isTentative,
            metadata: node.__metadata,
          });
        }
      });
      return items;
    },
  }), [editor]);

  return null;
}

// Plugin to update editor content when value prop changes externally
function SyncValuePlugin({ value }: { value: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Don't sync if value matches current content
    editor.getEditorState().read(() => {
      const currentText = $getRoot().getTextContent();
      if (currentText === value) return;
    });

    // Only sync when value is cleared (e.g., after submit)
    if (value === '') {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
      });
    }
  }, [editor, value]);

  return null;
}

function extractTextContent(editorState: EditorState): string {
  let text = '';
  editorState.read(() => {
    const root = $getRoot();
    text = root.getTextContent();
  });
  return text;
}

// Inner component that lives inside LexicalComposer
function EditorContent({
  onChange,
  onSubmit,
  placeholder,
  onFocusChange,
  value,
  imperativeRef,
}: {
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  onFocusChange?: (focused: boolean) => void;
  value: string;
  imperativeRef: React.RefObject<LexicalPromptInputHandle | null>;
}) {
  const handleChange = useCallback((editorState: EditorState) => {
    const text = extractTextContent(editorState);
    onChange(text);
  }, [onChange]);

  return (
    <>
      <PlainTextPlugin
        contentEditable={
          <ContentEditable
            className="lexical-content-editable w-full px-4 py-3 pr-12 bg-transparent text-gray-900 dark:text-white text-sm resize-none outline-none"
            style={{
              minHeight: '44px',
              maxHeight: '150px',
              overflowY: 'auto',
            }}
          />
        }
        placeholder={
          <div className="lexical-placeholder absolute top-3 left-4 text-gray-400 dark:text-gray-500 text-sm pointer-events-none select-none">
            {placeholder}
          </div>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <HistoryPlugin />
      <OnChangePlugin onChange={handleChange} />
      <EnterKeyPlugin onSubmit={onSubmit} />
      <FocusBlurPlugin onFocusChange={onFocusChange} />
      <SyncValuePlugin value={value} />
      <ImperativeHandlePlugin imperativeRef={imperativeRef} />
      <CommandPlugin />
      <TentativeItemPlugin />
    </>
  );
}

const LexicalPromptInput = forwardRef<LexicalPromptInputHandle, LexicalPromptInputProps>(({
  value,
  onChange,
  onSubmit,
  placeholder = 'Type a message...',
  disabled = false,
  onFocusChange,
}, ref) => {
  const imperativeRef = useRef<LexicalPromptInputHandle | null>(null);

  // Forward the ref
  useImperativeHandle(ref, () => ({
    insertItem: (payload: PromptItemPayload) => {
      imperativeRef.current?.insertItem(payload);
    },
    focus: () => {
      imperativeRef.current?.focus();
    },
    getEditor: () => {
      return imperativeRef.current?.getEditor() || null;
    },
    getPromptItems: () => {
      return imperativeRef.current?.getPromptItems() || [];
    },
  }), []);

  const initialConfig = {
    namespace: 'PromptInput',
    theme: {
      root: 'lexical-root',
      paragraph: 'lexical-paragraph',
    },
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    nodes: [PromptItemNode],
    editable: !disabled,
  };

  return (
    <div className="relative lexical-editor-wrapper">
      <LexicalComposer initialConfig={initialConfig}>
        <EditorContent
          onChange={onChange}
          onSubmit={onSubmit}
          placeholder={placeholder}
          onFocusChange={onFocusChange}
          value={value}
          imperativeRef={imperativeRef}
        />
      </LexicalComposer>

      <style>{`
        .lexical-root {
          position: relative;
        }

        .lexical-paragraph {
          margin: 0;
        }

        .lexical-content-editable {
          position: relative;
        }

        .lexical-content-editable:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
});

LexicalPromptInput.displayName = 'LexicalPromptInput';

export default LexicalPromptInput;
